import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheckCircleOutline, MdDownload, MdOutlineRemoveCircleOutline } from "react-icons/md";

import lessonServices from "../../../services/lessonServices";
import studentServices from "../../../services/studentServices";
import Button from "../../Button";
import Modal from "../../Modal";
import ApproveModal from "../teacher/ApproveModal";
import DisapproveModal from "../teacher/DisapproveModal";

const CodeNotificationLevelProjectModal = forwardRef(({ selectedProgram, onSubmit }, ref) => {
  const { t } = useTranslation();

  const [selectedLessonID, setSelectedLessonID] = useState("");
  const [studentName, setStudentName] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const [projectData, setProjectData] = useState(null); // stores the project info
  const [lastStatusId, setLastStatusId] = useState(null); // status for approve/disapprove buttons

  const approveModalRef = useRef();
  const disApproveModalRef = useRef();

  const openApproveModal = (projectID) => {
    approveModalRef.current?.openModal(projectID);
  };

  const openDisapproveModal = (projectID) => {
    disApproveModalRef.current?.openModal(projectID);
  };

  const handleApproveModalRefSuccess = () => {
    getCodeSolution(); // refresh after approval
  };

  const handleDisapproveModalRefSuccess = () => {
    getCodeSolution(); // refresh after disapproval
  };

  useImperativeHandle(ref, () => ({
    openModal(lessonID, action_by) {
      setSelectedLessonID(lessonID);
      setStudentName(action_by);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const getCodeSolution = async () => {
    if (!selectedLessonID) return;
    try {
      const response = await studentServices.getStudentProject(selectedLessonID);
      setProjectData(response);
      setLastStatusId(response?.last_status_id || null);
    } catch (error) {
      console.error("Error fetching project data:", error);
    }
  };

  const downloadFile = async (payload) => {
    if (!payload?.file_submitted) return;

    const url = payload.file_submitted;
    const urlParts = url.split("?");
    const pathParts = urlParts[0].split("/");
    const originalName = pathParts[pathParts.length - 1];
    const extensionMatch = originalName.match(/\.(\w+)$/);
    const extension = extensionMatch ? extensionMatch[0] : "";

    const fileName = `${studentName || "file"}-${payload?.name || "submission"}${extension}`;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  useEffect(() => {
    if (isOpen && selectedLessonID) {
      getCodeSolution();
    }
  }, [selectedLessonID, isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        footer={
          <div className="flex justify-end">
            <Button variant="text" onClick={handleCloseModal} label={t("close")} />
          </div>
        }
      >
        {projectData ? (
          <div className="flex justify-center">
            <div className="flex items-center gap-6 rounded-xl border border-gray-200 bg-gray-50 p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {(lastStatusId === 2 || lastStatusId === 5) && (
                <div className="flex flex-col items-center">
                  <Button
                    onClick={() => openApproveModal(projectData.id)}
                    tooltip={t("approve")}
                    icon={<MdCheckCircleOutline size={28} className="text-green-500" />}
                    variant="text"
                  />
                  <span className="mt-1 text-xs font-medium text-green-600">{t("approve")}</span>
                </div>
              )}

              {(lastStatusId === 2 || lastStatusId === 5) && (
                <div className="flex flex-col items-center">
                  <Button
                    onClick={() => openDisapproveModal(projectData.id)}
                    tooltip={t("disapprove")}
                    icon={<MdOutlineRemoveCircleOutline size={28} className="text-red-500" />}
                    variant="text"
                  />
                  <span className="mt-1 text-xs font-medium text-red-600">{t("disapprove")}</span>
                </div>
              )}

              {projectData?.file_submitted && (
                <div className="flex flex-col items-center">
                  <Button
                    tooltip={t("download")}
                    onClick={() => downloadFile(projectData)}
                    icon={<MdDownload size={28} className="text-blue-500" />}
                    variant="text"
                  />
                  <span className="mt-1 text-xs font-medium text-blue-600">{t("download")}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center p-6">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
            <span className="ml-2 text-sm text-gray-500">{t("loading")}...</span>
          </div>
        )}
      </Modal>

      <ApproveModal ref={approveModalRef} onSubmit={handleApproveModalRefSuccess} />
      <DisapproveModal ref={disApproveModalRef} onSubmit={handleDisapproveModalRefSuccess} />
    </>
  );
});

export default CodeNotificationLevelProjectModal;
